"use client";

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ConfigProvider } from "./config";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { canEdit } from "./auth/roles";
import { EditBar } from "./components/EditBar";
import { DemoLock } from "./lock/DemoLock";
import { RegistryProvider } from "./blocks/context";
import { getDb } from "./firebase/client";
import { collection, onSnapshot } from "firebase/firestore";
import { createRegistry, mergeRegistry, type BlockDefinition } from "./blocks/registry";
import { CustomBlockSchema, type CustomBlock as CustomBlockData } from "./schema/customBlocks";
import { customBlockToDefinition } from "./blocks/customTemplate/definition";
import { resolveModules } from "./modules/registry";
import { ModulesProvider } from "./modules/context";
import { applyBlockOverrides, type BlockOverrides } from "./modules/overrides";
import { builtinModules } from "./modules/builtins";
import type { ModuleDefinition, ModulesSiteConfig } from "./modules/types";
import { ConsentBanner, type ConsentSettings } from "./modules/consent/ConsentBanner";
import { EditorProvider } from "./editor/EditorProvider";
import { SiteSettingsProvider, useSiteSettings, useSiteSettingsLiveSync } from "./settings/useSiteSettings";
import { mergeThemeTokens, type ThemeTokens, type PublicSiteSettings } from "./schema/settings";
import { initAppCheck } from "./firebase/appcheck";
import { DemoStudio } from "./demo/DemoStudio";
import { setDemoActive } from "./demo/demoStore";
import { Theme, type DefinedTheme } from "@astryxdesign/core/theme";
import { neutralTheme } from "@astryxdesign/theme-neutral/built";
import { LinkProvider, type LinkComponentType } from "@astryxdesign/core/Link";
import { ImageProvider, type ImageComponentType } from "./media/ImageContext";
import { validateSiteConfig, type SiteConfig } from "./schema/siteConfig";
import { validateModuleContract } from "./modules/contract";

interface ProviderProps {
  config: SiteConfig;
  /** Losse klant-blokken (via `defineBlock`) buiten een module om. Altijd actief. */
  blocks?: BlockDefinition[];
  /**
   * Klant-modules (via `defineModule`) bovenop de ingebouwde. Worden gemerged op `id` (klant wint)
   * en respecteren `config.modules` voor activatie.
   */
  modules?: ModuleDefinition[];
  /**
   * Per-site presentatie-overrides per block-type (`{ gallery: { Render: MijnGalerij } }`). Vervangt
   * alleen het uiterlijk; schema/editor/logica blijven van core. De krachtigste maatwerk-naad.
   */
  overrides?: BlockOverrides;
  /**
   * SSR-startset van custom blocks (5.0): geef het resultaat van `getSiteCustomBlocks(siteId)`
   * (server) door zodat runtime-gedefinieerde blokken al in de server-HTML zitten. Zonder deze
   * prop laadt de provider ze alsnog client-side (werkend, maar pas na hydratie zichtbaar).
   */
  customBlocksData?: CustomBlockData[];
  /**
   * SSR-startset van de publieke site-instellingen: geef het resultaat van
   * `getPublicSiteSettings(siteId)` (server) door zodat klant-thema/nav/footer al in de
   * server-HTML zitten (geen theme-flits bij eerste paint) én publiek geen client-read meer
   * doet — de ISR-gecachte layout ververst de settings per revalidate. Zonder deze prop laadt
   * de provider ze alsnog client-side (werkend, maar met mogelijke flits + 1 read per bezoeker).
   */
  settingsData?: PublicSiteSettings;
  /**
   * Astryx-basistheme voor de hele site (radius-/spacing-/neutralen-karakter). Standaard `neutral`.
   * Een site kan een ander pakket kiezen (`@astryxdesign/theme-butter`, `-stone`, …) of een eigen
   * `defineTheme(...)` doorgeven voor component-overrides — zónder core te forken.
   */
  astryxTheme?: DefinedTheme;
  /**
   * Framework-link-component (bv. Next `Link`) voor soepele client-navigatie + prefetch. Astryx-
   * componenten met een `href` (knoppen/links/tabs) gebruiken 'm dan i.p.v. een rauwe `<a>` (= volledige
   * page-reload). Core importeert `next/link` niet zelf (optionele peer) — geef 'm hier door vanuit de site.
   */
  linkComponent?: LinkComponentType;
  /**
   * Framework-beeldcomponent (bv. Next `Image`) voor automatische beeldoptimalisatie (responsive
   * srcset, AVIF/WebP, geen layout-shift). Core importeert `next/image` niet zelf — geef 'm hier door.
   */
  imageComponent?: ImageComponentType;
  children: ReactNode;
}

// Stabiele lege defaults: een `= []`-defaultparameter maakt bij ELKE render een nieuwe
// array-referentie en laat memo's/effects met `modules`/`blocks` in de deps onnodig hervuren.
const NO_BLOCKS: BlockDefinition[] = [];
const NO_MODULES: ModuleDefinition[] = [];

// Dev-waarschuwingen één keer per boodschap (React strict-mode dubbelt effects; zonder dedupe
// verschijnt dezelfde config-waarschuwing telkens opnieuw in de console).
const warned = new Set<string>();
function warnOnce(message: string) {
  if (warned.has(message)) return;
  warned.add(message);
  console.warn(message);
}

/**
 * Wikkel de hele site in deze provider (in `app/layout.tsx`). Hij zet:
 * - de SiteConfig-context (siteId, theme, mode) + live publieke settings (footer/nav/modules);
 * - auth + content (Firebase, env-gedreven; werkt ook zonder Firebase);
 * - de block-registry (ingebouwde + klant-blokken) voor de page-renderer;
 * - de themavariabelen als CSS custom properties;
 * - App Check (alleen als geconfigureerd) en de EditBar/DemoLock.
 */
export function XiCMSProvider({ config, blocks = NO_BLOCKS, modules = NO_MODULES, overrides, astryxTheme, linkComponent, imageComponent, customBlocksData, settingsData, children }: ProviderProps) {
  useEffect(() => {
    initAppCheck();
  }, []);

  // Dev-vangnet voor (agent-)geschreven configs: meld config- en module-contractfouten meteen in
  // de console i.p.v. een subtiel kapotte site. In productie geen werk — en er wordt nooit gegooid.
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    const configIssues = validateSiteConfig(config);
    if (configIssues.length > 0) {
      warnOnce(`[xicms] site.config.ts bevat problemen:\n- ${configIssues.join("\n- ")}`);
    }
    if (modules.length > 0) {
      const contractIssues = validateModuleContract(modules);
      if (contractIssues.length > 0) {
        warnOnce(`[xicms] klant-modules voldoen niet aan het module-contract:\n- ${contractIssues.join("\n- ")}`);
      }
    }
  }, [config, modules]);

  return (
    <ConfigProvider config={config}>
      <SiteSettingsProvider siteId={config.siteId} initial={settingsData}>
        <ModuleLayer config={config} blocks={blocks} modules={modules} overrides={overrides} astryxTheme={astryxTheme} linkComponent={linkComponent} imageComponent={imageComponent} customBlocksData={customBlocksData}>
          {children}
        </ModuleLayer>
      </SiteSettingsProvider>
    </ConfigProvider>
  );
}

/**
 * Theme-tokens → CSS custom properties. Puur; wordt in `ModuleLayer` op het LIVE-gemergde thema
 * toegepast (SiteConfig + `settings/public.theme`), zodat huisstijl-wijzigingen uit /beheer direct
 * op de site verschijnen — net als module-instellingen.
 */
function themeCssVars(theme: ThemeTokens): Record<string, string> {
  // Dark-auto via light-dark(): deze variabelen staan INLINE op `.xicms-root`, en inline wint
  // altijd van het `@media (prefers-color-scheme: dark)`-blok in styles.css. Alleen dat blok kan
  // een inline lichte waarde dus nooit overrulen — de tokenwaarde zelf moet meeschakelen.
  // (styles.css zet `color-scheme: light dark` op `[data-dark="auto"]`, vereist voor light-dark().)
  const auto = Boolean(theme.dark?.auto);
  const ld = (light: string, dark: string) => (auto ? `light-dark(${light}, ${dark})` : light);
  const cssVars: Record<string, string> = {
    "--xicms-primary": theme.dark?.primary ? ld(theme.primary, theme.dark.primary) : theme.primary,
    "--xicms-accent": theme.accent ?? theme.primary,
    "--xicms-bg": ld(theme.background ?? "#ffffff", theme.dark?.background ?? "#0f172a"),
    "--xicms-fg": ld(theme.foreground ?? "#0f172a", theme.dark?.foreground ?? "#e2e8f0"),
  };
  if (theme.fontFamily) cssVars["--xicms-font"] = theme.fontFamily;
  // Optionele schalen → CSS-variabelen (alleen gezet als opgegeven; styles.css heeft defaults).
  for (const [k, v] of Object.entries(theme.spacing ?? {})) if (v) cssVars[`--xicms-spacing-${k}`] = v;
  if (theme.radius?.sm) cssVars["--xicms-radius-sm"] = theme.radius.sm;
  if (theme.radius?.md) cssVars["--xicms-radius"] = theme.radius.md;
  if (theme.radius?.lg) cssVars["--xicms-radius-lg"] = theme.radius.lg;
  if (theme.container?.maxWidth) cssVars["--xicms-container-max"] = theme.container.maxWidth;
  if (theme.container?.padding) cssVars["--xicms-container-pad"] = theme.container.padding;
  if (theme.dark?.background) cssVars["--xicms-bg-dark"] = theme.dark.background;
  if (theme.dark?.foreground) cssVars["--xicms-fg-dark"] = theme.dark.foreground;
  if (theme.dark?.primary) cssVars["--xicms-primary-dark"] = theme.dark.primary;

  // Astryx-tokens op XiCMS-waarden mappen, zodat de Astryx-componenten elke site z'n eigen merk
  // (kleur/typografie/radius) volgen. Inline op `.xicms-root` → wint over de theme.css-defaults.
  cssVars["--color-accent"] = "var(--xicms-primary)";
  // Tekst óp de accentkleur: wit op een donkere merkkleur, donker op een lichte (geel/pastel) —
  // hardcoded wit maakte knop- en bannertekst onleesbaar op lichte primaire kleuren.
  const onAccent = isDarkColor(theme.primary) ? "#ffffff" : "#0f172a";
  cssVars["--color-on-accent"] = onAccent;
  cssVars["--xicms-on-primary"] = onAccent;
  cssVars["--color-text-accent"] = "var(--xicms-primary)";
  cssVars["--radius-element"] = "var(--xicms-radius-sm, 8px)";
  cssVars["--radius-container"] = "var(--xicms-radius, 14px)";
  cssVars["--radius-page"] = "var(--xicms-radius-lg, 24px)";
  // Volledige paletmapping — ALLEEN als de site een eigen achtergrond/tekst kiest. Zo verschilt niet
  // enkel het accent maar de hele sfeer (kaarten, borders, secundaire tekst) per site; een minimale
  // config blijft op de gepolijste neutrale basis (incl. automatische dark-mode via light-dark()).
  if (theme.background) {
    cssVars["--color-background-body"] = "var(--xicms-bg)";
    cssVars["--color-background-surface"] = "color-mix(in srgb, var(--xicms-fg) 4%, var(--xicms-bg))";
    cssVars["--color-background-card"] = "color-mix(in srgb, var(--xicms-fg) 3%, var(--xicms-bg))";
    cssVars["--color-background-popover"] = "var(--xicms-bg)";
    cssVars["--color-background-muted"] = "color-mix(in srgb, var(--xicms-fg) 6%, var(--xicms-bg))";
    // Ook de XiCMS-oppervlaktetokens meeschakelen: hun defaults in styles.css zijn licht (#fff/zwart-
    // rgba) en het dark-blok vuurt alleen bij `data-dark="auto"`. Zonder deze mapping tonen presets
    // met een vaste donkere achtergrond (donker-modern, luxe-donker) witte kaarten en zwarte borders.
    cssVars["--xicms-surface"] = "color-mix(in srgb, var(--xicms-fg) 3%, var(--xicms-bg))";
    cssVars["--xicms-muted"] = "color-mix(in srgb, var(--xicms-fg) 5%, var(--xicms-bg))";
  }
  if (theme.foreground) {
    cssVars["--color-text-primary"] = "var(--xicms-fg)";
    cssVars["--color-text-secondary"] = "color-mix(in srgb, var(--xicms-fg) 65%, transparent)";
    cssVars["--color-neutral"] = "var(--xicms-fg)";
    cssVars["--color-border"] = "color-mix(in srgb, var(--xicms-fg) 14%, transparent)";
    cssVars["--xicms-border"] = "color-mix(in srgb, var(--xicms-fg) 12%, transparent)";
    cssVars["--xicms-border-strong"] = "color-mix(in srgb, var(--xicms-fg) 22%, transparent)";
  }
  if (theme.headingFont) cssVars["--xicms-font-heading"] = theme.headingFont;
  if (theme.fontFamily) {
    cssVars["--font-family-body"] = "var(--xicms-font)";
    // Koppen volgen een apart display/serif-font als dat is opgegeven, anders het body-font.
    cssVars["--font-family-heading"] = theme.headingFont ? "var(--xicms-font-heading)" : "var(--xicms-font)";
  } else if (theme.headingFont) {
    cssVars["--font-family-heading"] = "var(--xicms-font-heading)";
  }

  return cssVars;
}

/**
 * Is deze (hex-)kleur donker? Bepaalt de Astryx-thememodus voor sites met een vaste donkere
 * achtergrond (bv. presets donker-modern/luxe-donker, `dark.auto: false`): zonder deze afleiding
 * bleef Astryx op "light" staan en tekenden zijn componenten lichte kaarten op een donkere pagina.
 * Puur en exporteerbaar voor tests; niet-hex waarden gelden als licht (veilige default).
 */
export function isDarkColor(color?: string): boolean {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color?.trim() ?? "");
  if (!m) return false;
  let hex = m[1];
  if (hex.length === 3) hex = hex.replace(/./g, (c) => c + c);
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.45;
}

/**
 * Binnenlaag: leest de live publieke settings en resolvet daarmee de modules (settings/public.modules
 * legt zich over SiteConfig.modules) én het thema (settings/public.theme over SiteConfig.theme).
 * Zo werken module-aan/uit, -instellingen én huisstijl die de klant in /beheer zet direct door.
 */
function ModuleLayer({
  config,
  blocks = NO_BLOCKS,
  modules = NO_MODULES,
  overrides,
  customBlocksData,
  astryxTheme,
  linkComponent,
  imageComponent,
  children,
}: ProviderProps) {
  const { settings } = useSiteSettings();

  // LIVE thema: wijzigingen uit /beheer (settings/public.theme) winnen per token van SiteConfig.
  const theme = useMemo(() => mergeThemeTokens(config.theme, settings.theme), [config.theme, settings.theme]);
  const cssVars = useMemo(() => themeCssVars(theme), [theme]);

  // Demo-bewerkmodus globaal markeren zodat de (Firebase-)dataclients lokaal-only werken.
  // In een effect (niet tijdens render): render-pure + veilig onder concurrent rendering. De
  // demoStore-default is `false`, dus vóór het effect gedraagt alles zich als niet-demo.
  useEffect(() => {
    setDemoActive(Boolean(config.demoEditing));
  }, [config.demoEditing]);

  const resolved = useMemo(
    () =>
      resolveModules([...builtinModules, ...modules], {
        modules: config.modules,
        firestoreModules: settings.modules as ModulesSiteConfig | undefined,
      }),
    [config.modules, modules, settings.modules],
  );

  // Custom blocks (5.0): runtime-gedefinieerde bloktypes uit sites/{siteId}/customBlocks.
  // Publieke bezoekers krijgen UITSLUITEND de SSR-startset (customBlocksData, server-side gelezen) —
  // géén Firestore-listener per bezoeker (kosten/verbinding). Alleen ingelogde editors krijgen een
  // live sync (CustomBlocksSync, binnen AuthProvider) zodat een nieuw blok uit /beheer of een
  // agent-push zonder redeploy in het toevoeg-menu en op de pagina verschijnt.
  const [customBlocks, setCustomBlocks] = useState<CustomBlockData[]>(customBlocksData ?? []);
  const customDefs = useMemo(() => customBlocks.map(customBlockToDefinition), [customBlocks]);

  const registry = useMemo(
    () => applyBlockOverrides(mergeRegistry(mergeRegistry(createRegistry(resolved.blocks), customDefs), blocks), overrides),
    [resolved.blocks, customDefs, blocks, overrides],
  );

  return (
    <ModulesProvider value={resolved}>
      <RegistryProvider registry={registry}>
        <AuthProvider siteId={config.siteId} demo={Boolean(config.demoEditing)}>
            <CustomBlocksSync siteId={config.siteId} onData={setCustomBlocks} />
            <SiteSettingsSync siteId={config.siteId} />
            <EditorProvider>
              {/* React 19 hijst <link rel="stylesheet"> automatisch naar <head> → lettertype laadt. */}
              {theme.fontUrl && <link rel="stylesheet" href={theme.fontUrl} />}
              {/* Astryx-thema (Neutral basis) — de XiCMS-token-mapping op `.xicms-root` legt elke
                  site z'n eigen merk eroverheen. */}
              <Theme theme={astryxTheme ?? neutralTheme} mode={theme.dark?.auto ? "system" : isDarkColor(theme.background) ? "dark" : "light"}>
                {/* Framework-router voor Astryx-links/knoppen (soepele client-nav + prefetch);
                    zonder `linkComponent` valt het terug op een gewone `<a>` (huidig gedrag). */}
                <LinkProvider component={linkComponent ?? "a"}>
                  <ImageProvider component={imageComponent}>
                  <div
                    className="xicms-root"
                    data-dark={theme.dark?.auto ? "auto" : undefined}
                    style={cssVars as React.CSSProperties}
                  >
                    <DemoStudio />
                    {children}
                    <EditBar />
                    <DemoLock />
                    {resolved.activeIds.has("consent") && (
                      <ConsentBanner settings={resolved.settings.consent as ConsentSettings} />
                    )}
                  </div>
                  </ImageProvider>
                </LinkProvider>
              </Theme>
            </EditorProvider>
        </AuthProvider>
      </RegistryProvider>
    </ModulesProvider>
  );
}

/**
 * Live sync van custom blocks — ALLEEN voor ingelogde editors. Publieke bezoekers krijgen de
 * SSR-startset (customBlocksData) en géén Firestore-listener: dat scheelt een collection-read +
 * open verbinding per bezoeker, conform het getDoc-voor-publiek-patroon van useSiteSettings/News.
 */
/**
 * Live sync van de publieke site-instellingen — ALLEEN voor ingelogde editors (zelfde patroon als
 * CustomBlocksSync). Zo ziet een editor een /beheer-wijziging (footer/nav/thema) meteen op de site,
 * terwijl publiek op de SSR-seed (settingsData) of de eenmalige getDoc blijft draaien.
 */
function SiteSettingsSync({ siteId }: { siteId: string }) {
  const { role } = useAuth();
  useSiteSettingsLiveSync(siteId, canEdit(role));
  return null;
}

function CustomBlocksSync({ siteId, onData }: { siteId: string; onData: (defs: CustomBlockData[]) => void }) {
  const { role } = useAuth();
  const editing = canEdit(role);
  useEffect(() => {
    if (!editing) return;
    const db = getDb();
    if (!db) return;
    return onSnapshot(
      collection(db, "sites", siteId, "customBlocks"),
      (snap) => {
        const defs: CustomBlockData[] = [];
        snap.forEach((d) => {
          const res = CustomBlockSchema.safeParse({ ...(d.data() as Record<string, unknown>), type: (d.data() as { type?: string }).type ?? d.id });
          if (res.success) defs.push(res.data);
        });
        onData(defs);
      },
      () => undefined, // leesfout → SSR-set blijft staan
    );
  }, [editing, siteId, onData]);
  return null;
}
